/**
 * Tiny in-app event bus for shell-level actions — Long-Day Review, Tier 1.
 *
 * The sidebar's "New card" button, the `c` hotkey, and the ⌘K palette all need
 * to open the same dialog, which is mounted once at the root. Rather than
 * thread callbacks through three unrelated trees, they emit an event and the
 * host listens. Payloads are typed here so a new action is one line.
 */
export interface UiEvents {
  /** Open the global New card dialog, optionally pre-scoped to a project. */
  'new-card': { projectId?: string; projectKey?: string; sectionId?: string };
  /** Open the ⌘K palette. */
  palette: { query?: string };
  /** Open the keyboard-shortcut help sheet. */
  help: Record<string, never>;
}

const PREFIX = 'slate:ui:';

export function emitUi<K extends keyof UiEvents>(
  name: K,
  detail: UiEvents[K] = {} as UiEvents[K],
): void {
  window.dispatchEvent(new CustomEvent(PREFIX + name, { detail }));
}

export function onUi<K extends keyof UiEvents>(
  name: K,
  handler: (detail: UiEvents[K]) => void,
): () => void {
  const listener = (event: Event): void => handler((event as CustomEvent<UiEvents[K]>).detail);
  window.addEventListener(PREFIX + name, listener);
  return () => window.removeEventListener(PREFIX + name, listener);
}
